/**
 * Fullscreen resize-handle management for the chat iframe.
 * Handles creation, positioning, drag events, show/hide/destroy of the
 * edge-dragging resize handles that appear in fullscreen mode.
 * Extracted from IframeManager to keep the class focused on lifecycle.
 */
import type { OverlayLayout } from '../types';
import { MessageTypes } from '../types';
import {
  createElement,
  removeElement,
  addEventListener,
  getViewportDimensions,
} from '../utils/domUtils';
import type { MessageBridge } from './MessageBridge';
import { injectCSS } from '../utils/styleUtils';

export type ResizeEdge = 'top' | 'right' | 'bottom' | 'left';

export interface ResizeHandleStyles {
  primaryColor: string;
  primaryLightColor: string;
  handleWidth: number;
}

/**
 * Mutable state owned by IframeManager, passed by reference so handler
 * mutations are visible without manual write-back.
 */
export interface ResizeMutableState {
  resizeHandles: Map<ResizeEdge, HTMLDivElement>;
  isDraggingResize: boolean;
  activeResizeEdge: ResizeEdge | null;
  resizeStartPos: { x: number; y: number };
  resizeStartDimensions: { width: number; height: number; top: number; left: number };
  resizeHandleStyles: ResizeHandleStyles | null;
  isInFullscreenMode: boolean;
  pendingParentResizeMessage: number | null;
}

export interface IframeResizeContext {
  iframe: HTMLIFrameElement | null;
  layout: OverlayLayout;
  messageBridge: MessageBridge | null;
  state: { loaded: boolean; communicating: boolean };
  eventCleanups: (() => void)[];
  resizeState: ResizeMutableState;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Set resize handle styles from ChatInterface theme
 */
export function setResizeHandleStyles(ctx: IframeResizeContext, styles: ResizeHandleStyles): void {
  ctx.resizeState.resizeHandleStyles = styles;

  // Update existing handles with new styles
  if (ctx.resizeState.resizeHandles.size > 0) {
    injectResizeHandleCSS(ctx);

    ctx.resizeState.resizeHandles.forEach((handle, edge) => {
      const isHorizontal = edge === 'top' || edge === 'bottom';
      if (isHorizontal) {
        handle.style.height = `${styles.handleWidth}px`;
      } else {
        handle.style.width = `${styles.handleWidth}px`;
      }
    });

    updateResizeHandlePositions(ctx);
  }
}

/**
 * Enter fullscreen mode — show resize handles
 */
export function enterFullscreenMode(ctx: IframeResizeContext): void {
  ctx.resizeState.isInFullscreenMode = true;

  if (ctx.resizeState.resizeHandles.size === 0) {
    createResizeHandles(ctx);
  } else {
    showResizeHandles(ctx);
  }
}

/**
 * Exit fullscreen mode — hide resize handles
 */
export function exitFullscreenMode(ctx: IframeResizeContext): void {
  ctx.resizeState.isInFullscreenMode = false;
  hideResizeHandles(ctx);
}

/**
 * Destroy resize handles and reset drag state
 */
export function destroyResizeHandles(ctx: IframeResizeContext): void {
  ctx.resizeState.resizeHandles.forEach(handle => {
    removeElement(handle);
  });
  ctx.resizeState.resizeHandles.clear();
  ctx.resizeState.isDraggingResize = false;
  ctx.resizeState.activeResizeEdge = null;
}

/**
 * Update resize handle positions to match iframe position
 */
export function updateResizeHandlePositions(ctx: IframeResizeContext): void {
  if (!ctx.iframe) return;

  const rect = ctx.iframe.getBoundingClientRect();
  const handleWidth = ctx.resizeState.resizeHandleStyles?.handleWidth || 8;

  ctx.resizeState.resizeHandles.forEach((handle, edge) => {
    switch (edge) {
      case 'top':
        handle.style.top = `${rect.top - handleWidth / 2}px`;
        handle.style.left = `${rect.left}px`;
        handle.style.width = `${rect.width}px`;
        break;
      case 'right':
        handle.style.top = `${rect.top}px`;
        handle.style.left = `${rect.right - handleWidth / 2}px`;
        handle.style.height = `${rect.height}px`;
        break;
      case 'bottom':
        handle.style.top = `${rect.bottom - handleWidth / 2}px`;
        handle.style.left = `${rect.left}px`;
        handle.style.width = `${rect.width}px`;
        break;
      case 'left':
        handle.style.top = `${rect.top}px`;
        handle.style.left = `${rect.left - handleWidth / 2}px`;
        handle.style.height = `${rect.height}px`;
        break;
    }
  });
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/**
 * Inject CSS for resize handle hover and drag effects
 */
function injectResizeHandleCSS(ctx: IframeResizeContext): void {
  const styles = ctx.resizeState.resizeHandleStyles;
  if (!styles) return;

  const css = `
      /* Resize handle hover and drag effects */
      .kaigentic-resize-handle:hover {
        background-color: ${styles.primaryLightColor} !important;
        opacity: 0.5 !important;
      }

      .kaigentic-resize-handle.dragging {
        background-color: ${styles.primaryColor} !important;
        opacity: 0.7 !important;
        transition: none !important;
      }
    `;

  injectCSS(css, 'kaigentic-resize-handle-styles');
}

/**
 * Create resize handles for fullscreen mode
 */
function createResizeHandles(ctx: IframeResizeContext): void {
  if (!ctx.iframe) return;

  const edges: ResizeEdge[] = ['top', 'right', 'bottom', 'left'];

  edges.forEach(edge => {
    const handle = createElement('div', {
      className: `kaigentic-resize-handle kaigentic-resize-handle--${edge}`,
      styles: getResizeHandleStyles(ctx, edge)
    });

    const mouseDownCleanup = addEventListener(handle, 'mousedown', (e: MouseEvent) => {
      handleResizeStart(ctx, edge, e);
    });
    ctx.eventCleanups.push(mouseDownCleanup);

    document.body.appendChild(handle);
    ctx.resizeState.resizeHandles.set(edge, handle);
  });

  injectResizeHandleCSS(ctx);
  updateResizeHandlePositions(ctx);
}

/**
 * Get styles for a resize handle
 */
function getResizeHandleStyles(ctx: IframeResizeContext, edge: ResizeEdge): Partial<CSSStyleDeclaration> {
  const isHorizontal = edge === 'top' || edge === 'bottom';
  const handleWidth = ctx.resizeState.resizeHandleStyles?.handleWidth || 8;

  return {
    position: 'fixed',
    zIndex: ((ctx.layout.zIndex || 9999) + 1).toString(),
    cursor: isHorizontal ? 'ns-resize' : 'ew-resize',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
    pointerEvents: 'auto',
    ...(isHorizontal ? {
      height: `${handleWidth}px`,
    } : {
      width: `${handleWidth}px`,
    })
  } as Partial<CSSStyleDeclaration>;
}

/**
 * Handle resize start
 */
function handleResizeStart(ctx: IframeResizeContext, edge: ResizeEdge, e: MouseEvent): void {
  e.preventDefault();
  e.stopPropagation();

  if (!ctx.iframe) return;

  ctx.resizeState.isDraggingResize = true;
  ctx.resizeState.activeResizeEdge = edge;
  ctx.resizeState.resizeStartPos = { x: e.clientX, y: e.clientY };

  const activeHandle = ctx.resizeState.resizeHandles.get(edge);
  if (activeHandle) {
    activeHandle.classList.add('dragging');
  }

  const rect = ctx.iframe.getBoundingClientRect();
  ctx.resizeState.resizeStartDimensions = {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    left: rect.left
  };

  const mouseMoveCleanup = addEventListener(document, 'mousemove', (e: MouseEvent) => {
    handleResizeMove(ctx, e);
  });
  const mouseUpCleanup = addEventListener(document, 'mouseup', () => {
    handleResizeEnd(ctx);
    mouseMoveCleanup();
    mouseUpCleanup();
  });

  document.body.style.userSelect = 'none';
  document.body.style.cursor = edge === 'top' || edge === 'bottom' ? 'ns-resize' : 'ew-resize';

  if (ctx.iframe) {
    ctx.iframe.style.pointerEvents = 'none';
  }
}

/**
 * Handle resize move
 */
function handleResizeMove(ctx: IframeResizeContext, e: MouseEvent): void {
  if (!ctx.resizeState.isDraggingResize || !ctx.resizeState.activeResizeEdge || !ctx.iframe) return;

  const deltaX = e.clientX - ctx.resizeState.resizeStartPos.x;
  const deltaY = e.clientY - ctx.resizeState.resizeStartPos.y;

  const viewport = getViewportDimensions();
  let newWidth = ctx.resizeState.resizeStartDimensions.width;
  let newHeight = ctx.resizeState.resizeStartDimensions.height;
  let newTop = ctx.resizeState.resizeStartDimensions.top;
  let newLeft = ctx.resizeState.resizeStartDimensions.left;

  switch (ctx.resizeState.activeResizeEdge) {
    case 'top':
      newTop = Math.max(0, Math.min(viewport.height - 100, ctx.resizeState.resizeStartDimensions.top + deltaY));
      newHeight = Math.max(100, ctx.resizeState.resizeStartDimensions.height - (newTop - ctx.resizeState.resizeStartDimensions.top));
      break;

    case 'bottom':
      newHeight = Math.max(100, Math.min(viewport.height - ctx.resizeState.resizeStartDimensions.top, ctx.resizeState.resizeStartDimensions.height + deltaY));
      break;

    case 'left':
      newLeft = Math.max(0, Math.min(viewport.width - 100, ctx.resizeState.resizeStartDimensions.left + deltaX));
      newWidth = Math.max(100, ctx.resizeState.resizeStartDimensions.width - (newLeft - ctx.resizeState.resizeStartDimensions.left));
      break;

    case 'right':
      newWidth = Math.max(100, Math.min(viewport.width - ctx.resizeState.resizeStartDimensions.left, ctx.resizeState.resizeStartDimensions.width + deltaX));
      break;
  }

  ctx.iframe.style.width = `${newWidth}px`;
  ctx.iframe.style.height = `${newHeight}px`;
  ctx.iframe.style.top = `${newTop}px`;
  ctx.iframe.style.left = `${newLeft}px`;

  updateResizeHandlePositions(ctx);

  sendThrottledParentResize(ctx, viewport);
}

/**
 * Send PARENT_RESIZE message with RAF-based throttling
 */
function sendThrottledParentResize(ctx: IframeResizeContext, viewport: { width: number; height: number }): void {
  if (ctx.resizeState.pendingParentResizeMessage !== null) {
    cancelAnimationFrame(ctx.resizeState.pendingParentResizeMessage);
  }

  ctx.resizeState.pendingParentResizeMessage = requestAnimationFrame(() => {
    if (ctx.messageBridge && ctx.state.loaded && ctx.state.communicating) {
      ctx.messageBridge.send(MessageTypes.PARENT_RESIZE, {
        width: viewport.width,
        height: viewport.height
      });
    }
    ctx.resizeState.pendingParentResizeMessage = null;
  });
}

/**
 * Handle resize end
 */
function handleResizeEnd(ctx: IframeResizeContext): void {
  ctx.resizeState.resizeHandles.forEach(handle => {
    handle.classList.remove('dragging');
  });

  ctx.resizeState.isDraggingResize = false;
  ctx.resizeState.activeResizeEdge = null;

  if (ctx.resizeState.pendingParentResizeMessage !== null) {
    cancelAnimationFrame(ctx.resizeState.pendingParentResizeMessage);
    ctx.resizeState.pendingParentResizeMessage = null;
  }

  document.body.style.cursor = '';
  document.body.style.userSelect = '';

  if (ctx.iframe) {
    ctx.iframe.style.pointerEvents = 'auto';
  }
}

/**
 * Show resize handles
 */
function showResizeHandles(ctx: IframeResizeContext): void {
  ctx.resizeState.resizeHandles.forEach(handle => {
    handle.style.display = 'block';
  });
  updateResizeHandlePositions(ctx);
}

/**
 * Hide resize handles
 */
function hideResizeHandles(ctx: IframeResizeContext): void {
  ctx.resizeState.resizeHandles.forEach(handle => {
    handle.style.display = 'none';
  });
}
